import Sentry from '@/sentry'
import { proxy, useSnapshot } from 'valtio'
import { devtools } from 'valtio/utils'

import { clearStore } from '@/lib/valtio/reset-store'

import { parseColumnSnapshot } from '~shared/rundown/column'
import type { Column } from '~shared/rundown/column'

import type { DocumentChange } from 'firebase/firestore'
import type { Snapshot } from 'valtio'

type Columns = Record<Column['id'], Column>

/**
 * Store for the `rundown/columns` subcollection.
 *
 * Holds only the column _documents_, keyed by id and unordered. Order lives on
 * `rundown.columns` (an array of ids), and the ordered view is derived from the
 * two in `column.derived.ts`.
 *
 * @remarks
 *   Should be treated as read-only, mutations happen through the API. That is
 *   why it is not exported, so it can only be modified inside this module. A
 *   read-only reference is exported for derived data.
 *
 * @warning The Firestore listener must stay the ONLY writer. Never write an API
 * response back in as an optimistic update. That is a second writer racing the
 * listener, and an older snapshot arriving after a newer PATCH response silently
 * reverts the user's edit.
 */
const columnsStore = proxy<Columns>({})

export const columnsStoreActions = {
  /**
   * Syncs the local store with the snapshot from the Firestore listener.
   *
   * The listener query filters on `deletedAt == null`, so a soft-deleted column
   * leaves the result set and arrives here as a `removed` change.
   */
  processDocChanges(changes: DocumentChange[]) {
    changes.forEach((change) => {
      // Delta: removed
      //
      // The removed document needs no parsing: its id is all the store needs to
      // drop it, and its body may already be gone.
      if (change.type === 'removed') {
        delete columnsStore[change.doc.id]
        return
      }

      // Delta: added | modified
      //
      // A single malformed document must not tear down the live session, so a
      // schema failure is reported and skipped rather than thrown.
      let column: Column
      try {
        column = parseColumnSnapshot(change.doc)
      } catch (error) {
        Sentry.captureException(error)
        return
      }

      const existing = columnsStore[column.id]
      if (existing && existing.type === column.type) {
        // Object.assign does the merge in place, so the proxy identity stays
        // stable and only the changed fields notify subscribers.
        Object.assign(existing, column)
      } else {
        // A new column, or a changed type. The type decides which keys exist
        // (only a select column has `options`), and Object.assign never removes
        // a key, so the entry is replaced rather than merged.
        columnsStore[column.id] = column
      }
    })
  },

  /**
   * Empties the store. Called when leaving a rundown.
   */
  resetStore() {
    clearStore(columnsStore)
  },
}

/**
 * Look up a column in `columnsStore` directly.
 *
 * @remarks
 *   Reading through the proxy (not a snapshot) is deliberate: this is what the
 *   derived layer calls, and valtio only tracks reads that go through the
 *   proxy.
 *
 * @returns undefined if the column document has not arrived or was deleted
 */
export function getColumn(columnId: string): Column | undefined {
  return columnsStore[columnId]
}

/**
 * The column store itself, for the derived layer and the store hooks.
 *
 * @remarks
 *   Exported as read-only to keep the single-writer rule enforceable by the
 *   type system: subscribing and reading is fine, assigning is not.
 */
export const columnsStoreReadonly: Readonly<Columns> = columnsStore

// =============== HOOKS

/**
 * Subscribe to a single column.
 *
 * @returns undefined if the column document has not arrived or was deleted
 */
export function useColumnSnapshot(
  columnId: string,
): Snapshot<Column> | undefined {
  const snap = useSnapshot(columnsStore)

  return snap[columnId]
}

// #region ============== DEV
if (process.env.NODE_ENV !== 'production') {
  devtools(columnsStore, { name: 'columnsStore', enabled: true })
}
// #endregion
